/**
 * Pokémon Type Definitions
 */

export interface PokemonStat {
  name: string;
  value: number;
}

export interface Pokemon {
  id: number;
  name: string;
  nameFr: string;
  types: string[];
  sprite: string;
  spriteShiny: string;
  spriteAnimated: string;
  stats: PokemonStat[];
  height: number;
  weight: number;
  category: string;
  description: string;
  generation: number;
  eggGroups: string[];
}

export type AIDifficulty = "easy" | "normal" | "hard";

export type PokemonType =
  | 'normal'
  | 'fire'
  | 'water'
  | 'electric'
  | 'grass'
  | 'ice'
  | 'fighting'
  | 'poison'
  | 'ground'
  | 'flying'
  | 'psychic'
  | 'bug'
  | 'rock'
  | 'ghost'
  | 'dragon'
  | 'dark'
  | 'steel'
  | 'fairy';

export interface TeamState {
  team1: Pokemon[];
  team2: Pokemon[];
  currentPlayer: 1 | 2;
}

export const MAX_TEAM_SIZE = 6;
export const STARTER_COUNT = 3;
export const MAX_POKEMON_ID = 1025;

// --- App State Types ---

export type Screen = "home" | "draft" | "battle" | "collection";

export type Tier = "ou" | "uu" | "ag" | "all";

export interface DraftSettings {
  enabledGenerations: number[];
  allowLegendaries: boolean;
  allowMythicals: boolean;
  typeFilter: string | null;
  cardsPerDraw: 3 | 4 | 5;
  maxRerolls: number;
  tier: Tier;
  blindMode: boolean;
  blindClues: BlindClues;
}

export interface BlindClues {
  showId: boolean;
  showTypes: boolean;
  showGeneration: boolean;
  showCategory: boolean;
  showDescription: boolean;
}

export interface PokemonTeam {
  pokemon: Pokemon[];
  name: string;
}

export interface SavedTeam {
  id: string;
  name: string;
  team: Pokemon[];
  date: string;
}

export interface AudioSettings {
  musicEnabled: boolean;
  sfxEnabled: boolean;
}

export interface AppState {
  screen: Screen;
  teams: [PokemonTeam, PokemonTeam];
  currentPlayer: 1 | 2;
  draftPool: number[];
  draftCards: Pokemon[];
  rerollsRemaining: [number, number];
  settings: DraftSettings;
  audio: AudioSettings;
  loading: boolean;
  error: string | null;
  mode: "2p" | "solo";
  aiDifficulty: AIDifficulty;
  savedTeams: SavedTeam[];
}

export type AppAction =
  | { type: "SET_SCREEN"; screen: Screen }
  | { type: "SET_DRAFT_CARDS"; cards: Pokemon[] }
  | { type: "SET_LOADING"; loading: boolean }
  | { type: "SET_ERROR"; error: string | null }
  | { type: "PICK_POKEMON"; pokemon: Pokemon }
  | { type: "REMOVE_POKEMON"; pokemonId: number; player: 1 | 2 }
  | { type: "CLEAR_TEAM"; player: 1 | 2 }
  | { type: "RESET_DRAFT" }
  | { type: "USE_REROLL" }
  | { type: "UPDATE_SETTINGS"; settings: Partial<DraftSettings> }
  | { type: "SET_DRAFT_POOL"; pool: number[] }
  | { type: "TOGGLE_MUSIC" }
  | { type: "TOGGLE_SFX" }
  | { type: "RESTORE_STATE"; teams: [PokemonTeam, PokemonTeam]; currentPlayer: 1 | 2 }
  | { type: "SET_MODE"; mode: "2p" | "solo"; difficulty?: AIDifficulty }
  | { type: "SAVE_TEAM"; team: SavedTeam }
  | { type: "DELETE_SAVED_TEAM"; id: string };

export const DEFAULT_SETTINGS: DraftSettings = {
  enabledGenerations: [1, 2, 3, 4, 5, 6, 7, 8, 9],
  allowLegendaries: true,
  allowMythicals: true,
  typeFilter: null,
  cardsPerDraw: 3,
  maxRerolls: 3,
  tier: "ou",
  blindMode: false,
  blindClues: {
    showId: true,
    showTypes: false,
    showGeneration: false,
    showCategory: false,
    showDescription: false,
  },
};
